import React, { useContext, useState, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { FaArrowRight, FaArrowLeft, FaCheckCircle, FaCloudUploadAlt, FaUser, FaGraduationCap, FaBook, FaPaperclip, FaFileSignature } from 'react-icons/fa';
import programsData from '../programsData';
import { TimeContext } from '../TimeContext';
import { trackApplicationStart, trackApplicationComplete } from '../analytics/events';
import './Homepage.css';
import { SiteHeader, SiteFooter } from './Homepage';

/* ─── Step definitions ─── */
const STEPS = [
  { id: 1, label: 'Personal',   icon: <FaUser />,          desc: 'Your personal & contact details' },
  { id: 2, label: 'Academic',   icon: <FaGraduationCap />, desc: 'Education background & experience' },
  { id: 3, label: 'Program',    icon: <FaBook />,          desc: 'Program choice & languages' },
  { id: 4, label: 'Documents',  icon: <FaPaperclip />,     desc: 'Upload supporting documents' },
  { id: 5, label: 'Declaration',icon: <FaFileSignature />, desc: 'Review & submit your application' },
];

/* ─── Reusable Field component ─── */
const EField = ({ label, name, type = 'text', as, children, placeholder, required = true, errors, touched, hint }) => {
  const isError = errors?.[name] && touched?.[name];
  return (
    <div className="enroll-field">
      <label htmlFor={name} className="enroll-label">
        {label}{required && ' *'}
      </label>
      {as === 'select' ? (
        <Field as="select" name={name} id={name} className={`enroll-input${isError ? ' error' : ''}`}>
          {children}
        </Field>
      ) : as === 'textarea' ? (
        <Field as="textarea" name={name} id={name} placeholder={placeholder} rows={4}
          className={`enroll-input${isError ? ' error' : ''}`} style={{ resize: 'vertical' }} />
      ) : (
        <Field type={type} name={name} id={name} placeholder={placeholder}
          className={`enroll-input${isError ? ' error' : ''}`}
          aria-required={required ? 'true' : undefined}
          aria-invalid={isError ? 'true' : 'false'}
          aria-describedby={isError ? `${name}-error` : undefined}
        />
      )}
      {hint && <p style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>{hint}</p>}
      <ErrorMessage name={name} component="p" id={`${name}-error`}
        style={{ color: '#EF4444', fontSize: 12, marginTop: 4 }} />
    </div>
  );
};

/* ─── Step Progress Bar ─── */
const StepBar = ({ step, setStep }) => (
  <div style={{ marginBottom: 40 }}>
    {/* Progress line */}
    <div style={{ height: 4, background: 'rgba(65,105,225,0.12)', borderRadius: 2, marginBottom: 28, position: 'relative', overflow: 'hidden' }}>
      <motion.div
        animate={{ width: `${((step - 1) / (STEPS.length - 1)) * 100}%` }}
        transition={{ duration: 0.4, ease: 'easeInOut' }}
        style={{ height: '100%', background: 'linear-gradient(90deg, #4169E1, #FBBF24, #9ADB02)', borderRadius: 2 }}
      />
    </div>
    {/* Step dots */}
    <div style={{ display: 'flex', justifyContent: 'space-between', overflowX: 'auto', gap: 4, paddingBottom: 4 }}>
      {STEPS.map((s) => (
        <div
          key={s.id}
          role={step > s.id ? 'button' : undefined}
          tabIndex={step > s.id ? 0 : undefined}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flex: 1, minWidth: 48, cursor: step > s.id ? 'pointer' : 'default' }}
          onClick={() => step > s.id && setStep(s.id)}
          onKeyDown={(e) => { if (step > s.id && (e.key === 'Enter' || e.key === ' ')) setStep(s.id); }}
        >
          <div style={{
            width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: step > s.id ? 14 : 15, fontWeight: 700, transition: 'all 0.3s',
            background: step > s.id
              ? 'linear-gradient(135deg, #9ADB02, #6BA300)'
              : step === s.id
                ? 'linear-gradient(135deg, #4169E1, #1E3A8A)'
                : 'rgba(65,105,225,0.08)',
            color: step >= s.id ? '#fff' : '#9CA3AF',
            border: step === s.id ? '3px solid rgba(65,105,225,0.3)' : '2px solid transparent',
            boxShadow: step === s.id ? '0 0 0 4px rgba(65,105,225,0.12)' : 'none',
          }}>
            {step > s.id ? <FaCheckCircle style={{ fontSize: 16, color: '#fff' }} /> : s.icon}
          </div>
          <span style={{ fontSize: 10, fontWeight: 600, color: step >= s.id ? '#1E3A8A' : '#9CA3AF', letterSpacing: '0.06em', textTransform: 'uppercase', textAlign: 'center', whiteSpace: 'nowrap' }}>
            {s.label}
          </span>
        </div>
      ))}
    </div>
  </div>
);

/* ─── File Upload Zone ─── */
const FileZone = ({ name, label, required, values, setFieldValue, errors, touched, accept = 'image/jpeg,application/pdf', hint }) => {
  const isError = errors?.[name] && touched?.[name];
  const file = values[name];
  return (
    <div className="enroll-field">
      <label className="enroll-label">{label}{required && ' *'}</label>
      <div
        role="button"
        tabIndex={0}
        onClick={() => document.getElementById(`file-${name}`).click()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') document.getElementById(`file-${name}`).click(); }}
        style={{
          border: `2px dashed ${isError ? '#EF4444' : file ? '#9ADB02' : 'rgba(65,105,225,0.3)'}`,
          borderRadius: 8, padding: '28px 20px', textAlign: 'center', cursor: 'pointer',
          background: file ? 'rgba(154,219,2,0.05)' : 'rgba(65,105,225,0.02)',
          transition: 'all 0.3s',
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = '#4169E1'; e.currentTarget.style.background = 'rgba(65,105,225,0.04)'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = file ? '#9ADB02' : isError ? '#EF4444' : 'rgba(65,105,225,0.3)'; e.currentTarget.style.background = file ? 'rgba(154,219,2,0.05)' : 'rgba(65,105,225,0.02)'; }}
      >
        <input type="file" id={`file-${name}`} accept={accept} style={{ display: 'none' }}
          onChange={e => setFieldValue(name, e.currentTarget.files[0])} />
        {file ? (
          <>
            <FaCheckCircle style={{ color: '#9ADB02', fontSize: 28, marginBottom: 8 }} />
            <div style={{ fontSize: 13, color: '#1E3A8A', fontWeight: 600 }}>{file.name}</div>
            <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>{(file.size / 1024).toFixed(0)} KB · Click to change</div>
          </>
        ) : (
          <>
            <FaCloudUploadAlt style={{ color: '#4169E1', fontSize: 32, marginBottom: 8 }} />
            <div style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>Click to upload or drag & drop</div>
            {hint && <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>{hint}</div>}
          </>
        )}
      </div>
      {isError && <p style={{ color: '#EF4444', fontSize: 12, marginTop: 4 }}>{errors[name]}</p>}
    </div>
  );
};

/* ════════════════════════════════════════════
   ENROLLMENT COMPONENT
════════════════════════════════════════════ */
const Enrollment = () => {
  const { t, i18n } = useTranslation();
  const currentTime = useContext(TimeContext);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);

  /* The application takes 10-15 minutes, so start-vs-finish is the
     college's clearest view of admissions drop-off. Fires once. */
  const applicationStarted = useRef(false);
  const handleFormStart = useCallback(() => {
    if (applicationStarted.current) return;
    applicationStarted.current = true;
    trackApplicationStart();
  }, []);
  const [currentStep, setCurrentStep] = useState(1);

  const navLinks = [
    { to: '/',            label: t('nav.home', 'Home'),        ariaLabel: t('nav.home', 'Home') },
    { to: '/programs',    label: t('nav.programs', 'Programs'), ariaLabel: t('nav.programs', 'Programs') },
    { to: '/campus-life', label: t('nav.campusLife', 'Campus Life'), ariaLabel: t('nav.campusLife', 'Campus Life') },
    { to: '/alumni',      label: t('nav.alumni', 'Alumni'),    ariaLabel: t('nav.alumni', 'Alumni') },
    { to: '/enrollment',  label: t('nav.enrollment', 'Enrollment'), ariaLabel: t('nav.enrollment', 'Enrollment') },
  ];

  const availableLanguages = useMemo(() => [
    'English', 'Swahili', 'French', 'Spanish', 'German',
    'Italian', 'Japanese', 'Chinese', 'Arabic', 'Sign Language',
  ], []);

  /* ─── Full Yup schema (unchanged from original) ─── */
  const validationSchema = useMemo(() => Yup.object({
    full_name: Yup.string().trim().required(t('form.full_name.required', 'Full name is required')),
    date_of_birth: Yup.date()
      .required(t('form.date_of_birth.required', 'Date of birth is required'))
      .test('age', t('form.date_of_birth.age', 'Must be at least 16 years old'), (value) => {
        if (!value) return false;
        const today = new Date();
        const birthDate = new Date(value);
        const age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          return age - 1 >= 16;
        }
        return age >= 16;
      }),
    nationality: Yup.string().required(t('form.nationality.required', 'Nationality is required')),
    national_id: Yup.string()
      .required(t('form.national_id.required', 'National ID/Passport required'))
      .matches(/^[0-9]{8}$|^[A-Z0-9]{5,15}$/, t('form.national_id.format', 'Invalid format')),
    phone_number: Yup.string()
      .required(t('form.phone_number.required', 'Phone number required'))
      .matches(/^\+2547[0-9]{8}$/, t('form.phone_number.format', 'Format: +2547XXXXXXXX')),
    email_address: Yup.string()
      .email(t('form.email_address.invalid', 'Invalid email'))
      .required(t('form.email_address.required', 'Email is required')),
    physical_address: Yup.string().required(t('form.physical_address.required', 'Address is required')),
    highest_education_level: Yup.string().required(t('form.highest_education_level.required', 'Education level required')),
    secondary_school_attended: Yup.string().required(t('form.secondary_school_attended.required', 'School name required')),
    preferred_program: Yup.string().required(t('form.preferred_program.required', 'Please select a program')),
    languages: Yup.string()
      .required(t('form.languages.required', 'Please select at least one language'))
      .test('non-empty', t('form.languages.min', 'Select at least one language'), (value) => value && value.trim().length > 0),
    has_work_experience: Yup.boolean().required(t('form.has_work_experience.required', 'This field is required')),
    work_experience_details: Yup.string().when('has_work_experience', {
      is: true,
      then: (schema) => schema.required(t('form.work_experience_details.required', 'Please describe your experience')),
      otherwise: (schema) => schema.nullable(),
    }),
    id_passport_copy: Yup.mixed()
      .required(t('form.id_passport_copy.required', 'ID/Passport copy is required'))
      .test('fileSize', t('form.id_passport_copy.fileSize', 'File must be under 5MB'), (value) => !value || value.size <= 5 * 1024 * 1024)
      .test('fileType', t('form.id_passport_copy.fileType', 'Only JPG or PDF accepted'), (value) => !value || ['image/jpeg', 'application/pdf'].includes(value.type)),
    kcse_certificate: Yup.mixed()
      .required(t('form.kcse_certificate.required', 'KCSE certificate is required'))
      .test('fileSize', t('form.kcse_certificate.fileSize', 'File must be under 10MB'), (value) => !value || value.size <= 10 * 1024 * 1024)
      .test('fileType', t('form.kcse_certificate.fileType', 'Only JPG or PDF accepted'), (value) => !value || ['image/jpeg', 'application/pdf'].includes(value.type)),
    declaration_accuracy: Yup.boolean().required().oneOf([true], t('form.declaration_accuracy.oneOf', 'You must declare accuracy')),
    declaration_consent: Yup.boolean().required().oneOf([true], t('form.declaration_consent.oneOf', 'Consent is required')),
    receive_updates: Yup.boolean().required(),
    signature: Yup.string().required(t('form.signature.required', 'Signature is required')),
    submission_date: Yup.date().required(t('form.submission_date.required', 'Date is required')),
  }), [t]);

  const initialValues = {
    full_name: '', date_of_birth: '', nationality: 'Kenyan', national_id: '',
    phone_number: '', email_address: '', physical_address: '',
    highest_education_level: '', secondary_school_attended: '',
    preferred_program: '', languages: '', has_work_experience: false,
    work_experience_details: '', id_passport_copy: null, kcse_certificate: null,
    declaration_accuracy: false, declaration_consent: false, receive_updates: false,
    signature: '', submission_date: new Date().toISOString().split('T')[0],
  };

  const handleSubmit = useCallback(async (values, { setSubmitting, setFieldError }) => {
    try {
      const formData = new FormData();
      const languagesArray = values.languages ? values.languages.split(',').map(l => l.trim()) : [];

      Object.keys(values).forEach((key) => {
        if (key === 'languages') {
          formData.append(key, JSON.stringify(languagesArray));
        } else if (['id_passport_copy', 'kcse_certificate'].includes(key)) {
          if (values[key] instanceof File) formData.append(key, values[key]);
        } else {
          formData.append(key, typeof values[key] === 'boolean' ? values[key].toString() : values[key] || '');
        }
      });

      /* Fallback must match Homepage.jsx and Alumni.jsx. This previously
         read 'http://localhost:5000', so whenever REACT_APP_API_URL was
         missing at build time, applications were posted to the visitor's
         own machine and silently failed. See docs/CRITICAL-ENV-BUG.md. */
      const apiUrl = process.env.REACT_APP_API_URL || 'https://santomas-backend.up.railway.app';
      await axios.post(`${apiUrl}/api/enrollment`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      /* Course name only. No applicant details reach Meta or Google -
         see docs/DATA-PROTECTION.md before adding any parameter here. */
      trackApplicationComplete(values.preferred_program);
      setSubmissionSuccess(true);
    } catch (error) {
      setFieldError('general', error.response?.data?.error || t('enrollment.error', 'An error occurred during submission. Please try again.'));
      console.error('Form submission error:', error.response?.data || error);
    } finally {
      setSubmitting(false);
    }
  }, [t]);

  const sectionStyle = {
    background: '#fff',
    border: '1px solid rgba(65,105,225,0.12)',
    borderRadius: 12,
    padding: '32px 36px',
    boxShadow: '0 4px 24px rgba(65,105,225,0.06)',
  };

  const sectionHeading = (icon, text) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24, paddingBottom: 16, borderBottom: '2px solid rgba(65,105,225,0.08)' }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #4169E1, #1E3A8A)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 14, flexShrink: 0 }}>
        {icon}
      </div>
      <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 900, color: '#1E3A8A', margin: 0 }}>{text}</h3>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: '#EEF1FC' }}>
      <Helmet>
        <title>{t('seo.enrollment.title', 'Enrollment - San Tomas Tourism College')}</title>
        <meta name="description" content={t('seo.enrollment.description', 'Apply to San Tomas Tourism College in Nairobi.')} />
        <meta name="keywords" content={t('seo.enrollment.keywords', 'San Tomas Tourism College, enrollment, Nairobi')} />
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&family=Fraunces:ital,wght@0,700;0,900;1,700;1,900&display=swap" rel="stylesheet" />
        <link rel="canonical" href="https://santomastourismcollege.com/enrollment" />
        <meta property="og:title" content="Enrollment - San Tomas Tourism College" />
        <meta property="og:image" content="/social-banner.jpg" />
      </Helmet>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .check-row { display:flex; align-items:flex-start; gap:12px; margin-bottom:14px; cursor:pointer; }
        .check-row input[type=checkbox] { accent-color:#4169E1; width:17px; height:17px; margin-top:2px; flex-shrink:0; cursor:pointer; }
        .check-row label { font-family:'Poppins',sans-serif; font-size:13px; color:#374151; line-height:1.6; cursor:pointer; }
      `}</style>

      <a href="#main-content" className="sr-only focus:not-sr-only">{t('a11y.skipToContent', 'Skip to main content')}</a>

      <SiteHeader t={t} i18n={i18n} navLinks={navLinks} />

      {/* ── HERO ── */}
      <section className="sttc-hero" style={{ minHeight: '38vh' }}>
        <div className="hero-grid" aria-hidden="true" />
        <div className="hero-geo" aria-hidden="true" />
        <div className="hero-circle-1" aria-hidden="true" />
        <div className="sttc-container" style={{ position: 'relative', zIndex: 2, paddingTop: 120, paddingBottom: 52 }}>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="section-eyebrow" style={{ color: 'rgba(251,191,36,0.9)' }}>Applications Open · {new Date().getFullYear()}</div>
            <h1 className="section-title" style={{ color: '#fff', fontSize: 'clamp(34px,5vw,56px)' }}>
              {t('enrollment.title', 'Apply to ')}
              <em style={{ color: '#FBBF24' }}>San Tomas</em>
            </h1>
            <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.78)', maxWidth: 480, lineHeight: 1.8, fontWeight: 300, marginTop: 12 }}>
              Complete the form below. Takes about 10–15 minutes. We'll confirm your application by email.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="sttc-stripe" />

      {/* ── FORM ── */}
      <section id="main-content" style={{ padding: '64px 0 96px' }}>
        <div className="sttc-container" style={{ maxWidth: 860 }}>

          {submissionSuccess ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              style={{ ...sectionStyle, textAlign: 'center', padding: '72px 40px' }}
            >
              <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(135deg, #9ADB02, #6BA300)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: 32, color: '#fff' }}>
                <FaCheckCircle />
              </div>
              <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: 32, color: '#1E3A8A', marginBottom: 12, fontWeight: 900 }}>
                {t('enrollment.success', 'Application Submitted!')}
              </h2>
              <p style={{ fontSize: 15, color: '#6B7280', lineHeight: 1.8, maxWidth: 480, margin: '0 auto 36px' }}>
                Thank you for applying to San Tomas Tourism College. Our admissions team will review your application and contact you within 3–5 business days.
              </p>
              <Link to="/" className="btn-primary" style={{ textDecoration: 'none', display: 'inline-flex' }}>
                {t('enrollment.backToHome', 'Return to Home')} <FaArrowRight />
              </Link>
            </motion.div>
          ) : (
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ values, setFieldValue, errors, touched, isSubmitting }) => (
                <Form onFocusCapture={handleFormStart}>
                  <Field type="hidden" name="department" value="admissions" />

                  {/* Step progress */}
                  <StepBar step={currentStep} setStep={setCurrentStep} />

                  {/* Step panels */}
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentStep}
                      initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      {/* ── STEP 1: Personal ── */}
                      {currentStep === 1 && (
                        <div style={sectionStyle}>
                          {sectionHeading(<FaUser />, t('enrollment.section1', 'Personal Details'))}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px,1fr))', gap: '0 24px' }}>
                            <EField name="full_name" label={t('form.full_name.label','Full Name')} placeholder="Your full legal name" errors={errors} touched={touched} />
                            <EField name="date_of_birth" label={t('form.date_of_birth.label','Date of Birth')} type="date" errors={errors} touched={touched} />
                            <EField name="nationality" label={t('form.nationality.label','Nationality')} as="select" errors={errors} touched={touched}>
                              <option value="Kenyan">Kenyan</option>
                              <option value="Ugandan">Ugandan</option>
                              <option value="Tanzanian">Tanzanian</option>
                              <option value="Other">Other</option>
                            </EField>
                            <EField name="national_id" label={t('form.national_id.label','National ID / Passport No.')} placeholder={t('form.national_id.placeholder','Enter ID or Passport Number')} errors={errors} touched={touched} />
                            <EField name="phone_number" label={t('form.phone_number.label','Phone Number')} type="tel" placeholder="+2547XXXXXXXX" errors={errors} touched={touched} />
                            <EField name="email_address" label={t('form.email_address.label','Email Address')} type="email" placeholder="your@email.com" errors={errors} touched={touched} />
                            <div style={{ gridColumn: '1 / -1' }}>
                              <EField name="physical_address" label={t('form.physical_address.label','Physical Address')} placeholder={t('form.physical_address.placeholder','Enter your address')} errors={errors} touched={touched} />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* ── STEP 2: Academic ── */}
                      {currentStep === 2 && (
                        <div style={sectionStyle}>
                          {sectionHeading(<FaGraduationCap />, t('enrollment.section2', 'Academic Background'))}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px,1fr))', gap: '0 24px' }}>
                            <EField name="highest_education_level" label={t('form.highest_education_level.label','Highest Education Level')} as="select" errors={errors} touched={touched}>
                              <option value="">{t('form.select','Select an option')}</option>
                              <option value="KCSE">KCSE</option>
                              <option value="A-level">A-level</option>
                              <option value="Diploma">Diploma</option>
                              <option value="Degree">Degree</option>
                            </EField>
                            <EField name="secondary_school_attended" label={t('form.secondary_school_attended.label','Secondary School Attended')} errors={errors} touched={touched} />
                          </div>

                          <div className="enroll-field" style={{ marginTop: 8 }}>
                            <label className="enroll-label">{t('form.has_work_experience.label','Do you have work experience?')} *</label>
                            <div style={{ display: 'flex', gap: 20 }}>
                              {['true', 'false'].map(v => (
                                <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontFamily: "'Poppins',sans-serif", fontSize: 13, color: '#374151' }}>
                                  <input
                                    type="radio" name="has_work_experience" value={v}
                                    checked={String(values.has_work_experience) === v}
                                    onChange={() => setFieldValue('has_work_experience', v === 'true')}
                                    style={{ accentColor: '#4169E1' }}
                                  />
                                  {v === 'true' ? t('form.yes', 'Yes') : t('form.no', 'No')}
                                </label>
                              ))}
                            </div>
                            <ErrorMessage name="has_work_experience" component="p" style={{ color: '#EF4444', fontSize: 12, marginTop: 4 }} />
                          </div>

                          {(values.has_work_experience === true || values.has_work_experience === 'true') && (
                            <EField name="work_experience_details" as="textarea" label={t('form.work_experience_details.label','Work Experience Details')} placeholder={t('form.work_experience_details.placeholder','Describe your work experience')} errors={errors} touched={touched} />
                          )}
                        </div>
                      )}

                      {/* ── STEP 3: Program ── */}
                      {currentStep === 3 && (
                        <div style={sectionStyle}>
                          {sectionHeading(<FaBook />, t('enrollment.section3', 'Program & Languages'))}
                          <EField name="preferred_program" label={t('form.preferred_program.label','Preferred Program')} as="select" errors={errors} touched={touched}>
                            <option value="">{t('form.select','Select an option')}</option>
                            {programsData.map(p => (
                              <option key={p.id} value={p.name}>{p.name}</option>
                            ))}
                          </EField>

                          <div className="enroll-field">
                            <label className="enroll-label">{t('form.languages.label','Languages Known')} *</label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                              {availableLanguages.map(lang => {
                                const selected = values.languages.split(',').includes(lang);
                                return (
                                  <label key={lang}
                                    style={{
                                      display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                                      padding: '8px 14px', borderRadius: 50, transition: 'all 0.25s',
                                      border: `1.5px solid ${selected ? '#4169E1' : 'rgba(65,105,225,0.2)'}`,
                                      background: selected ? 'rgba(65,105,225,0.08)' : 'transparent',
                                      fontFamily: "'Poppins',sans-serif", fontSize: 12, fontWeight: 600,
                                      color: selected ? '#1E3A8A' : '#6B7280',
                                    }}
                                  >
                                    <input
                                      type="checkbox" name="languages" value={lang}
                                      checked={selected}
                                      onChange={e => {
                                        const curr = values.languages ? values.languages.split(',').filter(Boolean) : [];
                                        if (e.target.checked) setFieldValue('languages', [...curr, lang].join(','));
                                        else setFieldValue('languages', curr.filter(l => l !== lang).join(','));
                                      }}
                                      style={{ accentColor: '#4169E1', width: 14, height: 14 }}
                                    />
                                    {lang}
                                  </label>
                                );
                              })}
                            </div>
                            <ErrorMessage name="languages" component="p" style={{ color: '#EF4444', fontSize: 12, marginTop: 6 }} />
                          </div>
                        </div>
                      )}

                      {/* ── STEP 4: Documents ── */}
                      {currentStep === 4 && (
                        <div style={sectionStyle}>
                          {sectionHeading(<FaPaperclip />, t('enrollment.section4', 'Supporting Documents'))}
                          <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 24, lineHeight: 1.7 }}>
                            Please upload clear copies of the required documents. All files must be JPG or PDF format.
                          </p>
                          <FileZone name="id_passport_copy" label={t('form.id_passport_copy.label','ID / Passport Copy')} required values={values} setFieldValue={setFieldValue} errors={errors} touched={touched} hint="JPG or PDF, max 5MB" />
                          <FileZone name="kcse_certificate" label={t('form.kcse_certificate.label','KCSE Certificate')} required values={values} setFieldValue={setFieldValue} errors={errors} touched={touched} hint="JPG or PDF, max 10MB" />
                        </div>
                      )}

                      {/* ── STEP 5: Declaration ── */}
                      {currentStep === 5 && (
                        <div style={sectionStyle}>
                          {sectionHeading(<FaFileSignature />, t('enrollment.section5', 'Declaration & Consent'))}

                          <div style={{ background: 'rgba(65,105,225,0.04)', border: '1px solid rgba(65,105,225,0.1)', borderRadius: 8, padding: 20, marginBottom: 28 }}>
                            <p style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.8 }}>
                              By submitting this application, you confirm that all information provided is accurate and complete. San Tomas Tourism College will use this information solely for enrollment processing purposes.
                            </p>
                          </div>

                          <div style={{ marginBottom: 24 }}>
                            {[
                              { name: 'declaration_accuracy', label: t('form.declaration_accuracy.label','I declare that all information provided is accurate and complete.') + ' *' },
                              { name: 'declaration_consent', label: t('form.declaration_consent.label','I consent to the processing of my personal data for enrollment purposes.') + ' *' },
                              { name: 'receive_updates', label: t('form.receive_updates.label','I wish to receive updates, news and promotions from San Tomas Tourism College.') },
                            ].map(decl => (
                              <div key={decl.name} className="check-row">
                                <Field type="checkbox" name={decl.name}
                                  aria-invalid={errors[decl.name] ? 'true' : 'false'}
                                  aria-describedby={errors[decl.name] ? `${decl.name}-error` : undefined} />
                                <label htmlFor={decl.name}>{decl.label}</label>
                              </div>
                            ))}
                            <ErrorMessage name="declaration_accuracy" component="p" style={{ color: '#EF4444', fontSize: 12, marginBottom: 4 }} />
                            <ErrorMessage name="declaration_consent" component="p" style={{ color: '#EF4444', fontSize: 12 }} />
                            <p style={{ fontSize: 11, color: '#9CA3AF', fontStyle: 'italic', marginTop: 8 }}>
                              {t('form.pdpa_note','Your data will be processed in accordance with our privacy policy.')}
                            </p>
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px,1fr))', gap: '0 24px' }}>
                            <EField name="signature" label={t('form.signature.label','Full Name as Signature')} placeholder={t('form.signature.placeholder','Type your full legal name')} errors={errors} touched={touched} />
                            <EField name="submission_date" label={t('form.submission_date.label','Submission Date')} type="date" errors={errors} touched={touched} />
                          </div>

                          {errors.general && (
                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                              style={{ background: '#FEE2E2', border: '1px solid #EF4444', borderRadius: 8, padding: '12px 16px', marginTop: 8 }}>
                              <p style={{ color: '#B91C1C', fontSize: 13 }}>{errors.general}</p>
                            </motion.div>
                          )}
                        </div>
                      )}
                    </motion.div>
                  </AnimatePresence>

                  {/* ── Navigation ── */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24 }}>
                    <button
                      type="button"
                      onClick={() => setCurrentStep(s => Math.max(1, s - 1))}
                      disabled={currentStep === 1}
                      className="btn-outline"
                      style={{ opacity: currentStep === 1 ? 0.35 : 1, cursor: currentStep === 1 ? 'not-allowed' : 'pointer' }}
                    >
                      <FaArrowLeft style={{ fontSize: 11 }} /> Previous
                    </button>

                    {currentStep < STEPS.length ? (
                      <button
                        type="button"
                        onClick={() => setCurrentStep(s => Math.min(STEPS.length, s + 1))}
                        className="btn-primary"
                        style={{ border: 'none', cursor: 'pointer' }}
                      >
                        Next Step <FaArrowRight style={{ fontSize: 11 }} />
                      </button>
                    ) : (
                      <motion.button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn-primary"
                        style={{ border: 'none', cursor: isSubmitting ? 'not-allowed' : 'pointer', opacity: isSubmitting ? 0.75 : 1 }}
                        aria-label={t('form.submit','Submit Application')}
                        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                      >
                        {isSubmitting ? (
                          <>
                            <span style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(30,58,138,0.3)', borderTopColor: '#1E3A8A', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />
                            {t('form.submitting', 'Submitting...')}
                          </>
                        ) : (
                          <>{t('form.submit', 'Submit Application')} <FaArrowRight style={{ fontSize: 11 }} /></>
                        )}
                      </motion.button>
                    )}
                  </div>

                  {/* Step info pill */}
                  <p style={{ textAlign: 'center', marginTop: 16, fontSize: 12, color: '#9CA3AF', fontFamily: "'Poppins',sans-serif" }}>
                    Step {currentStep} of {STEPS.length} — {STEPS[currentStep - 1].desc}
                  </p>
                </Form>
              )}
            </Formik>
          )}
        </div>
      </section>

      <SiteFooter t={t} currentTime={currentTime} />
    </div>
  );
};

export default Enrollment;